<template>
  <div class="w-full">
    <div class="flex w-full flex-col gap-1 rounded-lg overflow-y-hidden dark:text-yapgray-200">
      <Disclosure v-for="(item, idx) in items" :key="item.key" v-slot="{ open, close }">
        <DisclosureButton
          class="group flex w-full items-center justify-between px-2 py-4 pl-1 hover:z-10 hover:text-primary-500 hover:shadow-lg dark:hover:text-primary-dark-300"
          :class="{
            'text-yapgray-500': item.disabled,
            'bg-white dark:bg-yapdarkgray-800': item.key !== state.selectedItemKey,
            'border-l-4 border-primary-500 dark:border-primary-dark-600': item.key === state.selectedItemKey,
            'bg-primary-50 dark:bg-primary-dark-700/30': item.key === state.selectedItemKey
          }"
          @click="click(item, close)"
        >
          <div
            class="flex w-full items-center justify-between gap-3 px-3.5"
            :ref="
              (el) => {
                accordionItems[idx] = el;
              }
            "
          >
            <slot name="trigger" :item="item" v-if="!!$slots.trigger" />
            <template v-else>
              <YapIcon v-if="item.icon" :name="item.icon" class="h-8 w-8 text-yapgray-800 dark:text-yapgray-200" />
              <img
                v-else-if="item.imageUri"
                :src="item.imageUri"
                :alt="item.imageAltText"
                class="w-12"
                :class="{ 'opacity-50': item.disabled, 'rounded-full': item.imageCircle }"
                loading="lazy"
              />
              <div class="flex w-full flex-col text-left">
                <h4 class="font-semibold">{{ $t(item.title) }}</h4>
                <div class="text-yapdarkgray-400 group-hover:text-primary-300/60" v-if="item.subtitle">
                  {{ $t(item.subtitle) }}
                </div>
              </div>
              <YapIcon
                :name="item.iconRight"
                class="mr-4 h-8 w-8 text-yapgray-400 dark:text-yapgray-200"
                v-if="item.iconRight"
              />
            </template>
          </div>
        </DisclosureButton>
        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-out"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <DisclosurePanel
            v-if="item.key === state.selectedItemKey && !item.noDisclosePanel"
            static
            class="rounded-lg border border-yapgray-300 p-4 dark:border-yapdarkgray-700"
          >
            <slot :item="item"></slot>
          </DisclosurePanel>
        </transition>
      </Disclosure>
      <div ref="endOfAccordionItemsRef"></div>
    </div>
  </div>
</template>

<script lang="ts">
export interface AccordionItem {
  key: string;
  title: string;
  subtitle?: string;
  icon?: string;
  iconRight?: string;
  imageUri?: string;
  imageAltText?: string;
  disabled?: boolean;
  imageCircle?: boolean;
  type?: string;
  status?: string;
  noDisclosePanel?: boolean;
}
</script>
<script setup lang="ts">
import { computed, onBeforeUpdate, reactive, ref, Ref, useTemplateRef } from 'vue';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';

import router from '@/router/router';
import YapIcon from '@/components/YapIcon.vue';
import { useIntersectionObserver } from '@vueuse/core';

const props = defineProps<{
  items: Array<AccordionItem>;
  customSelect?: boolean;
  customSelectId?: string;
  selectedItem?: string;
}>();

const emit = defineEmits(['selectItem', 'endOfItemsReached']);

const endOfAccordionItemsRef = useTemplateRef('endOfAccordionItemsRef');
const endOfAccordionItemsRefIsVisible = ref(false);
const { pause, resume } = useIntersectionObserver(endOfAccordionItemsRef, ([{ isIntersecting }]) => {
  endOfAccordionItemsRefIsVisible.value = isIntersecting;
  if (isIntersecting) {
    emit('endOfItemsReached');
  }
});

interface State {
  closeOther?: { (ref?: Ref | HTMLElement): void };
  selectedItemKey?: string;
}
const state: State = reactive({
  closeOther: undefined,
  selectedItemKey: computed(() =>
    props.selectedItem
      ? props.selectedItem
      : props.customSelectId
        ? `${router.currentRoute.value.params[props.customSelectId]}`
        : `${router.currentRoute.value.params.id}`
  )
});

const accordionItems = ref<Array<any>>([]);
onBeforeUpdate(() => {
  accordionItems.value = [];
});

function click(item: any, close: (ref?: Ref | HTMLElement) => void) {
  let itemKey = item.key;
  if (item.key === state.selectedItemKey) {
    close();
    itemKey = undefined;
  } else {
    if (state.closeOther) {
      state.closeOther();
    }
    state.closeOther = close;
  }
  if (props.customSelect) {
    emit('selectItem', itemKey);
    const index = props.items.findIndex((item) => item.key === itemKey);
    // Not perfect but better than not doing it...
    accordionItems.value[index]?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  } else {
    router.push({ params: { id: itemKey } });
  }
}
</script>
